import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import { KvkkNoticeModal, TermsModal, PrivacyModal } from '../components/LegalModals';
import { useTranslation } from '../i18n/LanguageContext';
import { useTheme } from '../hooks/useTheme';
import './Home.css';

const FEATURE_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const STRATEGY_KEYS = [1, 2, 3, 4];
const STEP_KEYS = [1, 2, 3, 4];
const TRUST_KEYS = [1, 2, 3, 4];

export default function Home({ isAuthenticated }) {
  const { t, lang, setLang } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [legalModal, setLegalModal] = useState(null); // 'kvkk' | 'terms' | 'privacy' | null

  const primaryTo = isAuthenticated ? '/panel' : '/login';
  const primaryLabel = t('home.ctaLogin');
  const year = new Date().getFullYear();

  return (
    <div className="ts-lp">
      <header className="ts-lp-topbar">
        <Link to="/" className="ts-lp-topbar-brand">
          <BrandLogo className="ts-lp-topbar-logo" imgClassName="ts-lp-topbar-logo-img" fallbackClassName="ts-lp-topbar-logo-fallback" />
          <span className="ts-lp-topbar-name">TrendSniper</span>
        </Link>
        <nav className="ts-lp-topbar-nav" aria-label="Sayfa içi gezinme">
          <a href="#ozellikler">{t('home.navFeatures')}</a>
          <a href="#stratejiler">{t('home.navStrategies')}</a>
          <a href="#surec">{t('home.navWorkflow')}</a>
        </nav>
        <div className="ts-lp-topbar-actions">
          <button type="button" className="ts-lp-icon-btn" onClick={() => setLang(lang === 'tr' ? 'en' : 'tr')}>
            {lang === 'tr' ? 'EN' : 'TR'}
          </button>
          <button type="button" className="ts-lp-icon-btn" onClick={toggleTheme}>
            {theme === 'light' ? '🌙' : '☀️'}
          </button>
          <Link to={primaryTo} className="ts-lp-btn ts-lp-btn-primary ts-lp-topbar-cta">
            {primaryLabel}
          </Link>
        </div>
      </header>

      <main>
        <section className="ts-lp-hero">
          <div className="ts-lp-hero-copy">
            <span className="ts-lp-eyebrow">{t('home.eyebrow')}</span>
            <h1>
              {t('home.heroTitleBefore')}<span>{t('home.heroTitleHighlight')}</span>{t('home.heroTitleAfter')}
            </h1>
            <p className="ts-lp-hero-lead">{t('home.heroLead')}</p>
            <div className="ts-lp-hero-actions">
              <Link to={primaryTo} className="ts-lp-btn ts-lp-btn-primary ts-lp-btn-large">
                {primaryLabel}
              </Link>
              <a href="#ozellikler" className="ts-lp-btn ts-lp-btn-ghost ts-lp-btn-large">
                {t('home.ctaExplore')}
              </a>
              <a
                href="https://uygarteknoloji.com.tr/urunler/detay.php?slug=https%3A%2F%2Ftrendsniper.com.tr&lang=tr"
                target="_blank"
                rel="noopener noreferrer"
                className="ts-lp-btn ts-lp-btn-buy ts-lp-btn-large"
              >
                {t('home.ctaBuy')}
              </a>
            </div>
            <ul className="ts-lp-chip-row">
              {['chip1', 'chip2', 'chip3', 'chip4', 'chip5', 'chip6'].map((k, i) => (
                <li key={k} className={`ts-lp-chip ts-lp-chip-${i % 4}`}>{t(`home.${k}`)}</li>
              ))}
            </ul>
          </div>

          <div className="ts-lp-ticket" aria-hidden="true">
            <div className="ts-lp-ticket-hole" />
            <div className="ts-lp-ticket-row ts-lp-ticket-head">
              <span className="ts-lp-ticket-brand">{t('home.ticketLabel')}</span>
              <span className="ts-lp-ticket-side">{t('home.ticketSide')}</span>
            </div>
            <div className="ts-lp-ticket-symbol">{t('home.ticketSymbol')}</div>
            <div className="ts-lp-ticket-perf" />
            <dl className="ts-lp-ticket-body">
              <div><dt>{t('home.ticketStrategyLabel')}</dt><dd>{t('home.ticketStrategyValue')}</dd></div>
              <div><dt>{t('home.ticketLeverageLabel')}</dt><dd>{t('home.ticketLeverageValue')}</dd></div>
              <div><dt>{t('home.ticketStatusLabel')}</dt><dd>{t('home.ticketStatusValue')}</dd></div>
            </dl>
            <div className="ts-lp-ticket-perf" />
            <div className="ts-lp-ticket-row ts-lp-ticket-foot">
              <span className="ts-lp-ticket-pnl-label">{t('home.ticketPnlLabel')}</span>
              <span className="ts-lp-ticket-pnl">+2.34%</span>
            </div>
          </div>
        </section>

        <section id="ozellikler" className="ts-lp-section ts-lp-features">
          <div className="ts-lp-section-head">
            <h2>{t('home.featuresTitle')}</h2>
            <p>{t('home.featuresLead')}</p>
          </div>
          <div className="ts-lp-feature-grid">
            {FEATURE_KEYS.map((n) => (
              <article key={n} className="ts-lp-feature-card">
                <h3>{t(`home.feature${n}Title`)}</h3>
                <p>{t(`home.feature${n}Desc`)}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="stratejiler" className="ts-lp-section ts-lp-strategies">
          <div className="ts-lp-section-head">
            <h2>{t('home.strategiesTitle')}</h2>
            <p>{t('home.strategiesLead')}</p>
          </div>
          <div className="ts-lp-strategy-grid">
            {STRATEGY_KEYS.map((n) => (
              <article key={n} className="ts-lp-strategy-card">
                <span className="ts-lp-strategy-badge">{String(n).padStart(2, '0')}</span>
                <h3>{t(`home.strategy${n}Name`)}</h3>
                <p>{t(`home.strategy${n}Desc`)}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="surec" className="ts-lp-section ts-lp-workflow">
          <div className="ts-lp-section-head">
            <h2>{t('home.workflowTitle')}</h2>
            <p>{t('home.workflowLead')}</p>
          </div>
          <ol className="ts-lp-workflow-steps">
            {STEP_KEYS.map((n, i) => (
              <li key={n} className="ts-lp-workflow-step">
                <div className="ts-lp-status-pill">{t(`home.step${n}Status`)}</div>
                <p>{t(`home.step${n}Desc`)}</p>
                {i < STEP_KEYS.length - 1 && <span className="ts-lp-workflow-arrow" aria-hidden="true" />}
              </li>
            ))}
          </ol>
        </section>

        <section className="ts-lp-section ts-lp-trust">
          <div className="ts-lp-section-head">
            <h2>{t('home.trustTitle')}</h2>
          </div>
          <ul className="ts-lp-trust-list">
            {TRUST_KEYS.map((n) => (
              <li key={n} className="ts-lp-trust-item">
                <span className="ts-lp-trust-check" aria-hidden="true">✓</span>
                <span>{t(`home.trustItem${n}`)}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="ts-lp-cta-banner">
          <h2>{t('home.ctaBannerTitle')}</h2>
          <p>{t('home.ctaBannerLead')}</p>
          <Link to={primaryTo} className="ts-lp-btn ts-lp-btn-primary ts-lp-btn-large">
            {primaryLabel}
          </Link>
        </section>
      </main>

      <footer className="ts-lp-footer">
        <div className="ts-lp-footer-top">
          <div className="ts-lp-footer-brand">
            <BrandLogo className="ts-lp-footer-logo" imgClassName="ts-lp-footer-logo-img" fallbackClassName="ts-lp-footer-logo-fallback" />
            <p>{t('home.footerTagline')}</p>
          </div>

          <nav className="ts-lp-footer-col" aria-label="Ürün">
            <h4>{t('home.footerProductHeading')}</h4>
            <a href="#ozellikler">{t('home.navFeatures')}</a>
            <a href="#stratejiler">{t('home.navStrategies')}</a>
            <a href="#surec">{t('home.navWorkflow')}</a>
            <Link to="/login">{t('home.ctaLogin')}</Link>
          </nav>

          <nav className="ts-lp-footer-col" aria-label="Kurumsal">
            <h4>{t('home.footerCorporateHeading')}</h4>
            <a href="https://uygarteknoloji.com.tr/#kurumsal" target="_blank" rel="noopener noreferrer">
              {t('home.footerAbout')}
            </a>
            <a href="https://uygarteknoloji.com.tr/#iletisim" target="_blank" rel="noopener noreferrer">
              {t('home.footerContact')}
            </a>
            <button type="button" className="ts-lp-footer-linklike" onClick={() => setLegalModal('privacy')}>
              {t('home.footerPrivacy')}
            </button>
            <button type="button" className="ts-lp-footer-linklike" onClick={() => setLegalModal('kvkk')}>
              {t('home.footerKvkk')}
            </button>
            <button type="button" className="ts-lp-footer-linklike" onClick={() => setLegalModal('terms')}>
              {t('home.footerTerms')}
            </button>
          </nav>
        </div>

        <div className="ts-lp-footer-bottom">
          <span>{t('home.footerRights', { year })}</span>
          <span>
            {' '}·{' '}
            <a href="https://uygarteknoloji.com.tr" target="_blank" rel="noopener noreferrer">
              Uygar Teknoloji
            </a>{' '}
            {t('home.footerBuiltBy')}
          </span>
        </div>
      </footer>

      {legalModal === 'kvkk' && <KvkkNoticeModal onClose={() => setLegalModal(null)} />}
      {legalModal === 'terms' && <TermsModal onClose={() => setLegalModal(null)} />}
      {legalModal === 'privacy' && <PrivacyModal onClose={() => setLegalModal(null)} />}
    </div>
  );
}
